/**
 * Per-user announcement popup: at most once per 24h while logged in (localStorage).
 * Cooldown uses the later of (last time a popup was shown) or (last dismiss), so
 * a refresh right after login does not show a second popup before 24h.
 */

const MS_24H = 24 * 60 * 60 * 1000;

function getUserKey() {
  try {
    const u = localStorage.getItem("username");
    if (u) return u;
    const raw = localStorage.getItem("user");
    if (raw) {
      const user = JSON.parse(raw);
      return user?.username || user?.userName || user?.email || "unknown";
    }
  } catch {
    // ignore
  }
  return "unknown";
}

function dismissedKey() {
  return `enviz_popup_dismissed_at_${getUserKey()}`;
}

function shownKey() {
  return `enviz_popup_shown_at_${getUserKey()}`;
}

function lastShownKey() {
  return `enviz_popup_last_id_${getUserKey()}`;
}

function parseIsoMs(raw) {
  if (raw == null || raw === "") return 0;
  const t = Date.parse(raw);
  return Number.isNaN(t) ? 0 : t;
}

/**
 * @returns {boolean} true if no popup was shown or dismissed within the last 24h
 */
export function shouldShowAnnouncementPopup() {
  const last = Math.max(
    parseIsoMs(localStorage.getItem(shownKey())),
    parseIsoMs(localStorage.getItem(dismissedKey())),
  );
  if (!last) return true;
  return Date.now() - last >= MS_24H;
}

/**
 * Call when an announcement is actually displayed (starts 24h throttle; avoids repeat on refresh).
 */
export function recordAnnouncementShown() {
  localStorage.setItem(shownKey(), new Date().toISOString());
}

/**
 * Call when user closes popup (X or backdrop).
 */
export function recordAnnouncementDismissed() {
  localStorage.setItem(dismissedKey(), new Date().toISOString());
}
/**
 * @returns {number|null}
 */
export function getLastShownPopupId() {
  const v = localStorage.getItem(lastShownKey());
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/**
 * @param {number} popupId
 */
export function setLastShownPopupId(popupId) {
  localStorage.setItem(lastShownKey(), String(popupId));
}

/**
 * After 24h cooldown, pick a random active popup (prefer not the same id as last time when possible).
 * @param {{ id: number }[]} activePopups
 * @returns {object | null}
 */
export function selectNextPopup(activePopups) {
  if (!activePopups || activePopups.length === 0) return null;
  const list = [...activePopups];
  const lastId = getLastShownPopupId();
  if (list.length === 1) return list[0];
  const others = lastId == null ? list : list.filter((p) => p.id !== lastId);
  const pool = others.length ? others : list;
  return pool[Math.floor(Math.random() * pool.length)];
}
